import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Edit2,
  Check,
  X as XIcon,
  Loader2,
  CheckCircle,
  XCircle,
  ArrowUpDown,
  FileText,
  Upload
} from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import {
  useListPortfolioSitesQuery,
  useListAdminSitesQuery,
  useUpdatePortfolioSiteMutation,
  useLazyGetPortfolioSiteQuery,
  useGetArtefactDownloadMutation,
  useLazyGetSiteArtefactsQuery,
  useLazyGetSiteValidationResultsQuery,
} from '../../redux/api/portfolioApi';
import { useGetOrganizationMembersQuery, useLazyGetOrganizationMembersQuery } from '../../redux/api/organizationApi';
import { setMembers } from '../../redux/slices/organizationSlice';
import { setCurrentSite } from '../../redux/slices/siteSlice';
import { formatDateTime } from '../../utils/dateUtils';
import { formatEmailDisplay } from '../../utils/emailUtils';
import { SessionManager } from '../../redux/sessionManager';
import { performGlobalLogout } from '../../utils/logoutUtils';
import { useDebounce } from '../../utils/debounce';
import toast from 'react-hot-toast';
import ThreeStepProgress from '../ThreeStepProgress';
import UploadConfirmationModal from '../UploadConfirmationModal';
import UploadAsBuiltModal from '../modals/UploadAsBuiltModal';
import { API_BASE_URL } from '../../config/apiConfig';
import {
  useLazyBulkExportExcelQuery,
  useRequestUploadMutation,
  useUploadCallbackMutation,
  useRequestBulkUploadMutation,
  BULK_UPLOAD_LIMITS,
  type UploadPipeline,
} from '../../redux/api/documentsApi';
import { streamProgress } from '../../utils/progressStream';
import { qaApi } from '../../redux/api/qaApi';

interface SiteListItem {
  id: string;
  name: string;
  asbuilt_name: string | null;
  created_at: string;
  uploaded_by: string | null;
  uploader?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
  } | null;
  portfolio_id: string;
  tenant_id: string;
  meta?: Record<string, any>;
}

type SortField = 'name' | 'created_at' | 'status';
type SortOrder = 'asc' | 'desc';

type ValidationStatus = 'loading' | 'processing' | 'passed' | 'warnings' | 'errors' | 'not_available' | 'no_extraction';

interface SiteValidationCache {
  [siteId: string]: {
    status: ValidationStatus;
    errorCount: number;
    warningCount: number;
  };
}

interface SiteQACache {
  [siteId: string]: {
    status: 'loading' | 'no_results' | 'processing' | 'completed' | 'failed';
    totalPassed?: number;
    totalFailed?: number;
    createdAt?: string;
  };
}

interface DemoUpload {
  id: string;
  fileName: string;
  step: 'upload' | 'processing' | 'done' | 'failed';
  siteId: string;
  documentId?: string | null;
  showRetry?: boolean;
  failureType?: 'upload' | 'processing';
}

interface ValidationStatusBadgeProps {
  site: SiteListItem;
  status: ValidationStatus;
  errorCount?: number;
  warningCount?: number;
  onClick?: () => void;
}

const ValidationStatusBadge: React.FC<ValidationStatusBadgeProps> = ({
  site,
  status,
  errorCount = 0,
  warningCount = 0,
  onClick
}) => {
  const baseClasses = "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer hover:scale-105 hover:shadow-lg";

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.();
  };

  const isAwaitingExtraction = site?.meta?.awaiting_asbuilt_extraction === true;
  const numWarnings = Number(warningCount) || 0;

  let displayStatus = status;
  if (isAwaitingExtraction) {
    displayStatus = 'processing';
  } else if (status !== 'loading' && status !== 'not_available' && status !== 'no_extraction') {
    if (status === 'errors') {
      displayStatus = 'errors';
    } else if (status === 'warnings' || numWarnings > 0) {
      displayStatus = 'warnings';
    } else {
      displayStatus = 'passed';
    }
  }

  switch (displayStatus) {
    case 'loading':
      return (
        <button onClick={handleClick} className={`${baseClasses} bg-[var(--color-surface)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]`}>
          <Loader2 className="w-3 h-3 animate-spin" />
          Loading
        </button>
      );
    case 'processing':
      return (
        <button onClick={handleClick} className={`${baseClasses} bg-engramiq-green/20 text-engramiq-green border border-engramiq-green/30 hover:bg-engramiq-green/30 hover:border-engramiq-green/50`}>
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span className="font-bold">Processing</span>
        </button>
      );
    case 'passed':
      return (
        <button onClick={handleClick} className={`${baseClasses} bg-engramiq-green/20 text-engramiq-green border border-engramiq-green/30 hover:bg-engramiq-green/30 hover:border-engramiq-green/50`}>
          <CheckCircle className="w-3.5 h-3.5" />
          <span className="font-bold">Passed</span>
        </button>
      );
    case 'warnings':
      return (
        <button onClick={handleClick} className={`${baseClasses} bg-engramiq-green/20 text-engramiq-green border border-engramiq-green/30 hover:bg-engramiq-green/30 hover:border-engramiq-green/50`}>
          <CheckCircle className="w-3.5 h-3.5" />
          <span className="font-bold">Passed</span>
          <span className="opacity-75">{numWarnings} Flag{numWarnings !== 1 ? 's' : ''}</span>
        </button>
      );
    case 'errors':
      return (
        <button onClick={handleClick} className={`${baseClasses} bg-accent-red/20 text-accent-red border border-accent-red/30 hover:bg-accent-red/30 hover:border-accent-red/50`}>
          <XCircle className="w-3.5 h-3.5" />
          <span className="font-bold">{errorCount}</span>
          <span>Error{errorCount !== 1 ? 's' : ''}</span>
        </button>
      );
    case 'no_extraction':
      return (
        <button
          type="button"
          onClick={handleClick}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-[var(--color-text-muted)] cursor-pointer hover:bg-[var(--color-surface-hover)] rounded-full transition-colors"
        >
          No component extraction
        </button>
      );
    case 'not_available':
    default:
      return (
        <button
          type="button"
          onClick={handleClick}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-[var(--color-text-muted)] cursor-pointer hover:bg-[var(--color-surface-hover)] rounded-full transition-colors"
        >
          No extraction run
        </button>
      );
  }
};

const SitesTableTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentSiteFromRedux = useAppSelector((reduxState) => reduxState.site.currentSite);
  const [sites, setSites] = useState<SiteListItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Use route to determine if validation fetching should run
  // Skip on routes where SitesTableTab is mounted but not the primary view
  const location = useLocation();
  const routesThatShouldNotFetchValidation = ['/component-view', '/component-metadata', '/network-viewer', '/as-built-viewer'];
  const shouldSkipValidationFetch = routesThatShouldNotFetchValidation.includes(location.pathname);

  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [appliedDateFrom, setAppliedDateFrom] = useState<string>('');
  const [appliedDateTo, setAppliedDateTo] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(20);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  const [validationCache, setValidationCache] = useState<SiteValidationCache>({});
  const [qaCache, setQaCache] = useState<SiteQACache>({});

  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [editedSiteName, setEditedSiteName] = useState<string>('');
  const [savingSiteName, setSavingSiteName] = useState(false);

  const [downloadingDocIds, setDownloadingDocIds] = useState<Set<string>>(new Set());
  const [exportingSiteIds, setExportingSiteIds] = useState<Set<string>>(new Set());

  const [demoUploads, setDemoUploads] = useState<DemoUpload[]>([]);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'demo' | 'failure' | 'failure-upload' | 'upload' | 'bulk-upload' | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);


  const authProfile = useAppSelector((s) => s.auth.profile);
  const organizationState = useAppSelector((s) => s.organization);
  const currentSite = useAppSelector((s) => s.site.currentSite);

  const userEmail = (authProfile.email || '').toLowerCase();
  const isSuperAdmin = userEmail.endsWith('@engramiq.com');
  const canUpload = isSuperAdmin || authProfile.can_upload === true;

  const rawTenant = authProfile.tenant as any;
  const userTenantId: string | null = useMemo(() => {
    if (!rawTenant) return null;
    if (typeof rawTenant === 'string') return rawTenant;
    if (typeof rawTenant === 'object') {
      return rawTenant.id || rawTenant.tenant_id || null;
    }
    return null;
  }, [rawTenant]);

  const currentOrgId = isSuperAdmin ? organizationState.currentOrganization?.id ?? null : null;
  const selectedPortfolioId = useAppSelector((state) => state.portfolio.selectedPortfolioId);
  const effectiveTenantId = isSuperAdmin ? currentOrgId : userTenantId;

  const uploadTenantId = useMemo(() => {
    if (isSuperAdmin) {
      return currentOrgId;
    } else {
      const activeTenantId = authProfile.active_tenant?.id || null;
      return activeTenantId || userTenantId;
    }
  }, [isSuperAdmin, currentOrgId, authProfile.active_tenant, userTenantId]);

  const adminArgs = isSuperAdmin
    ? {
        ...(currentOrgId ? { organization_id: currentOrgId } : {}),
        ...(appliedDateFrom ? { uploaded_after: `${appliedDateFrom}T00:00:00` } : {}),
        ...(appliedDateTo ? { uploaded_before: `${appliedDateTo}T23:59:59` } : {}),
        ...(debouncedSearchQuery ? { search: debouncedSearchQuery } : {}),
        limit: itemsPerPage,
        ...(currentCursor ? { cursor: currentCursor } : {}),
      }
    : undefined;

  const {
    data: adminSitesResponse,
    isLoading: adminLoading,
    error: adminSitesError,
    refetch: refetchAdminSites,
  } = useListAdminSitesQuery(adminArgs, {
    skip: !isSuperAdmin || !!selectedPortfolioId,
    refetchOnMountOrArgChange: true,
  });

  const [triggerGetMembers, { data: membersData }] = useLazyGetOrganizationMembersQuery();

  const organizationMembers = useAppSelector((s) => s.organization.members);

  const getMemberDisplayName = (member: any): string => {
    const user = member?.user;
    if (user) {
      const first = (user.first_name || '').trim();
      const last = (user.last_name || '').trim();
      const full = [first, last].filter(Boolean).join(' ');
      if (full) return full;
      if (user.email) return user.email;
    }
    return member.user_id || '';
  };

  const getMemberEmail = (member: any): string => {
    const user = member?.user;
    if (user?.email) return user.email;
    if (typeof member.user_id === 'string') return member.user_id;
    return '';
  };

  const selectedUploaderName = useMemo(() => {
    if (selectedUser === 'all') return undefined;
    const member = organizationMembers.find((m: any) => getMemberEmail(m) === selectedUser);
    if (!member) return undefined;
    
    const user = (member as any)?.user;
    if (user) {
      const firstName = (user.first_name || '').trim().toLowerCase();
      const lastName = (user.last_name || '').trim().toLowerCase();
      
      if (firstName) {
        return firstName;
      }
      if (lastName) {
        return lastName;
      }
      if (user.email) {
        return user.email.toLowerCase();
      }
    }
    
    return (member.user_id || '').toLowerCase() || undefined;
  }, [selectedUser, organizationMembers]);

  const {
    data: portfolioSitesResponse,
    isLoading: portfolioLoading,
    error: sitesError,
    refetch: refetchPortfolioSites,
  } = useListPortfolioSitesQuery(
    selectedPortfolioId
      ? {
          portfolio_id: selectedPortfolioId,
          uploader_name: selectedUploaderName,
          uploaded_after: appliedDateFrom ? `${appliedDateFrom}T00:00:00` : undefined,
          uploaded_before: appliedDateTo ? `${appliedDateTo}T23:59:59` : undefined,
          search: debouncedSearchQuery || undefined,
          limit: itemsPerPage,
          ...(currentCursor ? { cursor: currentCursor } : {}),
        }
      : { portfolio_id: '' },
    {
      skip: !selectedPortfolioId,
      refetchOnMountOrArgChange: true,
    }
  );

  const usePortfolioScoping = !!selectedPortfolioId;
  const loading = usePortfolioScoping ? portfolioLoading : (isSuperAdmin ? adminLoading : portfolioLoading);

  const [updatePortfolioSite, { isLoading: isUpdatingSite }] = useUpdatePortfolioSiteMutation();
  const [getDownloadUrl] = useGetArtefactDownloadMutation();
  const [triggerGetSiteArtefacts] = useLazyGetSiteArtefactsQuery();
  const [triggerGetValidation] = useLazyGetSiteValidationResultsQuery();
  const [triggerGetPortfolioSite] = useLazyGetPortfolioSiteQuery();
  const [triggerBulkExportExcel] = useLazyBulkExportExcelQuery();
  const [requestUpload] = useRequestUploadMutation();
  const [uploadCallback] = useUploadCallbackMutation();
  const [requestBulkUpload] = useRequestBulkUploadMutation();

  const sseAbortRef = useRef<Record<string, AbortController>>({});

  useEffect(() => {
    return () => {
      Object.values(sseAbortRef.current).forEach((controller) => controller.abort());
    };
  }, []);

  useEffect(() => {
    const raw = usePortfolioScoping ? portfolioSitesResponse : (isSuperAdmin ? adminSitesResponse : portfolioSitesResponse);

    if (!raw) {
      setSites([]);
      setTotalItems(0);
      setNextCursor(undefined);
      return;
    }

    // New response structure: { items: PortfolioSite[], pagination: SitesPaginationMetadata }
    const response = raw as { items: any[]; pagination: { total: number; limit: number; next_cursor?: string; has_more: boolean } };
    const sitesArray = response.items || [];
    const pagination = response.pagination;

    // Extract pagination metadata
    setTotalItems(pagination.total);
    setNextCursor(pagination.next_cursor);

    const transformedSites: SiteListItem[] = sitesArray.map((site: any) => ({
      id: site.id || site.site_id,
      name: site.name || site.site_name || 'Unknown Site',
      asbuilt_name: site.asbuilt_name || null,
      created_at: site.created_at,
      uploaded_by: site.uploaded_by || null,
      uploader: site.uploader || null,
      portfolio_id: site.portfolio_id,
      tenant_id: site.tenant_id,
      meta: site.meta || {},
    }));

    setSites(transformedSites);

    // Populate validation and QA caches from API response (avoids N+1 per-site requests)
    if (!shouldSkipValidationFetch && sitesArray.length > 0) {
      const newValidationCache: SiteValidationCache = {};
      const newQaCache: SiteQACache = {};
      sitesArray.forEach((site: any) => {
        const siteId = site.id || site.site_id;
        if (site.validation_result) {
          const v = site.validation_result;
          const hasValidatorsRun = Array.isArray(v.validators_run) && v.validators_run.length > 0;
          newValidationCache[siteId] = {
            status: !hasValidatorsRun && v.status !== 'not_available' ? 'no_extraction' : (v.status ?? 'not_available'),
            errorCount: Number(v.error_count) || 0,
            warningCount: Number(v.warning_count) || 0,
          };
        }
        if (site.drawing_qa) {
          const dq = site.drawing_qa;
          newQaCache[siteId] = {
            status: dq.status,
            ...(dq.status === 'completed' && {
              totalPassed: dq.total_passed,
              totalFailed: dq.total_failed,
            }),
          };
        }
      });
      if (Object.keys(newValidationCache).length > 0) {
        setValidationCache((prev) => ({ ...prev, ...newValidationCache }));
      }
      if (Object.keys(newQaCache).length > 0) {
        setQaCache((prev) => ({ ...prev, ...newQaCache }));
      }
    }

    const tenantIdForMembers = effectiveTenantId || (transformedSites.length > 0 ? transformedSites[0].tenant_id : null);
    if (tenantIdForMembers && sitesArray.length > 0) {
      triggerGetMembers({ tenant_id: tenantIdForMembers, _limit: 100, _offset: 0 }).catch((err) => {
        console.warn('Failed to fetch members:', err);
      });
    }

    // Only fetch validation results when this tab is the primary view
    // Skip on routes like /component-view where this panel may be mounted but not visible
    if (!shouldSkipValidationFetch) {
      transformedSites.forEach((site) => {
        if (!validationCache[site.id]) {
          setValidationCache((prev) => ({
            ...prev,
            [site.id]: { status: 'loading', errorCount: 0, warningCount: 0 },
          }));

          triggerGetValidation({ portfolio_id: site.portfolio_id, site_id: site.id })
            .unwrap()
            .then((result) => {
              const errorCount = result.errors?.length || 0;

              const allowedValidators = ['metadata_counts', 'power_flow_topology', 'graph_build', 'component_library_match', 'communications_monitoring'];
              let warningCount = 0;
              if (result.validator_details) {
                for (const validatorName of allowedValidators) {
                  const validatorDetail = result.validator_details[validatorName];
                  if (validatorDetail?.warnings) {
                    warningCount += validatorDetail.warnings.length;
                  }
                }
              }

              let status: ValidationStatus = 'passed';
              if (!result.validators_run || result.validators_run.length === 0) status = 'no_extraction';
              else if (errorCount > 0) status = 'errors';
              else if (warningCount > 0) status = 'warnings';

              setValidationCache((prev) => ({
                ...prev,
                [site.id]: { status, errorCount, warningCount },
              }));
            })
            .catch(() => {
              setValidationCache((prev) => ({
                ...prev,
                [site.id]: { status: 'not_available', errorCount: 0, warningCount: 0 },
              }));
            });
        }
      });
    }
  }, [adminSitesResponse, portfolioSitesResponse, isSuperAdmin, effectiveTenantId, triggerGetValidation, triggerGetMembers, shouldSkipValidationFetch]);

  // Fetch QA results for each site (Drawing QA column)
  useEffect(() => {
    if (!sites.length || shouldSkipValidationFetch) return;

    sites.forEach((site) => {
      if (qaCache[site.id]) return;

      setQaCache((prev) => ({ ...prev, [site.id]: { status: 'loading' } }));

      dispatch(
        qaApi.endpoints.listQAResultsNew.initiate({ site_id: site.id, limit: 1, offset: 0 })
      )
        .unwrap()
        .then((result) => {
          if (!result?.items?.length) {
            setQaCache((prev) => ({ ...prev, [site.id]: { status: 'no_results' } }));
            return;
          }

          const qaResult = result.items[0];
          if (qaResult.status !== 'completed') {
            setQaCache((prev) => ({ ...prev, [site.id]: { status: 'processing', createdAt: qaResult.created_at } }));
            return;
          }

          // Fetch executive summary for pass/fail counts
          dispatch(
            qaApi.endpoints.getQAResultExecutiveSummary.initiate(qaResult.id)
          )
            .unwrap()
            .then((summary) => {
              setQaCache((prev) => ({
                ...prev,
                [site.id]: {
                  status: 'completed',
                  totalPassed: summary.total_passed,
                  totalFailed: summary.total_failed,
                },
              }));
            })
            .catch(() => {
              setQaCache((prev) => ({ ...prev, [site.id]: { status: 'no_results' } }));
            });
        })
        .catch(() => {
          setQaCache((prev) => ({ ...prev, [site.id]: { status: 'no_results' } }));
        });
    });
  }, [sites, dispatch, shouldSkipValidationFetch]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setCurrentCursor(undefined);
    setNextCursor(undefined);
    setCursorHistory([]);
  }, [selectedUser, appliedDateFrom, appliedDateTo, debouncedSearchQuery, selectedPortfolioId, currentOrgId]);

  useEffect(() => {
    if (membersData) {
      const members = Array.isArray(membersData) 
        ? membersData 
        : (membersData as any)?.items || [];
      dispatch(setMembers(members));
    }
  }, [membersData, dispatch]);

  useEffect(() => {
    if (!sitesError) {
      setError(null);
      return;
    }
    const err = sitesError as any;
    const status = err?.status;
    if (status === 400 || status === 404) {
      setError(null);
      return;
    }
    setError('Failed to load sites');
  }, [sitesError]);

  // Note: Selected site is now fully managed by Redux (currentSite in siteSlice)

  const userOptions = useMemo(
    () => {
    const map = new Map<string, string>();
    organizationMembers.forEach((member: any) => {
      const email = getMemberEmail(member);
      if (!email) return;
      if (!map.has(email)) {
        map.set(email, getMemberDisplayName(member));
      }
    });

    const options = Array.from(map.entries()).map(([email, label]) => ({
      value: email,
      label,
    }));

    options.sort((a, b) => a.label.localeCompare(b.label));

      return [
        { value: 'all', label: 'All Users' },
        ...options,
      ];
    },
    [organizationMembers]
  );

  const filteredSites = useMemo(() => {
    let result = sites
      .filter((site) => {
        if (!dateFrom && !dateTo) return true;
        const siteDate = new Date(site.created_at);
        if (dateFrom && !dateTo) return siteDate >= new Date(dateFrom);
        if (!dateFrom && dateTo) return siteDate <= new Date(dateTo + 'T23:59:59');
        if (dateFrom && dateTo) {
          return siteDate >= new Date(dateFrom) && siteDate <= new Date(dateTo + 'T23:59:59');
        }
        return true;
      });

    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'status':
          const statusOrder = { errors: 0, warnings: 1, passed: 2, processing: 3, loading: 4, not_available: 5 };
          const aStatus = validationCache[a.id]?.status || 'not_available';
          const bStatus = validationCache[b.id]?.status || 'not_available';
          const aAwaiting = a.meta?.awaiting_asbuilt_extraction === true;
          const bAwaiting = b.meta?.awaiting_asbuilt_extraction === true;
          const aDisplayStatus = aAwaiting ? 'processing' : aStatus;
          const bDisplayStatus = bAwaiting ? 'processing' : bStatus;
          comparison = statusOrder[aDisplayStatus as keyof typeof statusOrder] - statusOrder[bDisplayStatus as keyof typeof statusOrder];
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [sites, selectedUser, selectedUploaderName, searchQuery, dateFrom, dateTo, sortField, sortOrder, validationCache]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (nextCursor) {
      // Save current cursor to history for back navigation
      if (currentCursor) {
        setCursorHistory(prev => [...prev, currentCursor]);
      }
      setCurrentCursor(nextCursor);
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (cursorHistory.length > 0) {
      // Go back to previous cursor
      const previousCursors = [...cursorHistory];
      const previousCursor = previousCursors.pop();
      setCursorHistory(previousCursors);
      setCurrentCursor(previousCursor);
      setCurrentPage(prev => Math.max(1, prev - 1));
    } else {
      // Go back to first page (no cursor)
      setCurrentCursor(undefined);
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page: number) => {
    // For cursor-based pagination, we can only navigate sequentially
    // So we'll navigate to the target page by going forward/backward
    if (page > currentPage) {
      // Need to go forward - this requires fetching pages sequentially
      // For now, we'll just go to next page if available
      if (nextCursor && page === currentPage + 1) {
        handleNextPage();
      }
    } else if (page < currentPage) {
      // Go back
      const pagesToGoBack = currentPage - page;
      if (pagesToGoBack <= cursorHistory.length) {
        // We have enough history to go back
        const newHistory = cursorHistory.slice(0, cursorHistory.length - pagesToGoBack);
        const targetCursor = pagesToGoBack === cursorHistory.length ? undefined : newHistory[newHistory.length - 1];
        setCursorHistory(newHistory);
        setCurrentCursor(targetCursor);
        setCurrentPage(page);
      } else {
        // Go back to first page
        setCursorHistory([]);
        setCurrentCursor(undefined);
        setCurrentPage(1);
      }
    }
  };

  const handleSiteSelect = async (siteId: string) => {
    const site = sites.find((s) => s.id === siteId);
    if (!site) return;

    const portfolioId = site.portfolio_id;
    if (!portfolioId) return;

    try {
      const fullSite = await triggerGetPortfolioSite({
        portfolio_id: portfolioId,
        site_id: siteId,
      }).unwrap();
      dispatch(setCurrentSite(fullSite));
    } catch (err) {
      console.error('Failed to fetch site details:', err);
    }
  };

  const startEditingSite = (siteId: string, currentName: string) => {
    setEditingSiteId(siteId);
    setEditedSiteName(currentName);
  };

  const cancelEditingSite = () => {
    setEditingSiteId(null);
    setEditedSiteName('');
  };

  const saveEditedSiteName = async (siteId: string) => {
    if (!editedSiteName.trim()) {
      toast.error('Site name cannot be empty');
      return;
    }

    let portfolioIdToUse: string | null = null;

    if (isSuperAdmin) {
      const site = sites.find((s) => s.id === siteId);
      if (!site || !site.portfolio_id) {
        toast.error('Site not found or missing portfolio ID');
        return;
      }
      portfolioIdToUse = site.portfolio_id;
    } else {
      if (!selectedPortfolioId) {
        toast.error('No portfolio selected');
        return;
      }
      portfolioIdToUse = selectedPortfolioId;
    }

    setSavingSiteName(true);
    try {
      const trimmedName = editedSiteName.trim();

      await updatePortfolioSite({
        portfolio_id: portfolioIdToUse,
        site_id: siteId,
        body: { name: trimmedName },
      }).unwrap();

      setSites((prevSites) =>
        prevSites.map((site) => (site.id === siteId ? { ...site, name: trimmedName } : site))
      );

      setEditingSiteId(null);
      setEditedSiteName('');
      toast.success('Site name updated successfully');
    } catch (err: any) {
      const errorMessage = err?.data?.detail || err?.message || 'Failed to update site name';
      toast.error(errorMessage);
    } finally {
      setSavingSiteName(false);
    }
  };

  const handleDownload = async (site: SiteListItem) => {
    setDownloadingDocIds(prev => new Set(prev).add(site.id));
    try {
      const artefactsData = await triggerGetSiteArtefacts({
        tenant_id: site.tenant_id,
        site_id: site.id,
      }).unwrap();

      const asBuiltArtefact = artefactsData?.items?.[0];

      if (!asBuiltArtefact) {
        throw new Error('Artefact not found for this site');
      }

      const result = await getDownloadUrl({
        document_id: asBuiltArtefact.id,
        tenant_id: site.tenant_id,
        expires_in: 1800,
      }).unwrap();

      const downloadUrl = result.url;
      const filename = `${site.name}.pdf`;

      if (!downloadUrl) {
        throw new Error('No download URL received');
      }

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Download started');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to download document');
    } finally {
      setDownloadingDocIds(prev => {
        const next = new Set(prev);
        next.delete(site.id);
        return next;
      });
    }
  };

  const handleExportExcel = async (site: SiteListItem) => {
    setExportingSiteIds(prev => new Set(prev).add(site.id));
    try {
      const csvBaseUrl = import.meta.env.VITE_CSV_BASE_URL;
      
      if (!csvBaseUrl) {
        throw new Error('CSV base URL is not configured');
      }

      const authHeaders = SessionManager.getAuthHeaders();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (authHeaders.Authorization) {
        headers['Authorization'] = authHeaders.Authorization;
      }

      const response = await fetch(`${csvBaseUrl}/convert`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          format: 'engramiq',
          site_id: site.id,
        }),
      });

      if (response.status === 401) {
        await performGlobalLogout(dispatch, {
          toastMessage: 'Session expired. Please sign in again.',
          toastType: 'error',
        });
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        throw new Error(`Failed to export CSV: ${response.statusText}`);
      }

      // Parse the JSON response
      const data = await response.json();
      
      if (!data.download_url || !data.file_name) {
        throw new Error('Invalid response format from CSV converter');
      }

      // Fetch the file from the presigned URL
      const fileResponse = await fetch(data.download_url);
      
      if (!fileResponse.ok) {
        throw new Error(`Failed to download CSV file: ${fileResponse.statusText}`);
      }

      const blob = await fileResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Extract extension from API response file_name and use site name
      const fileExtension = data.file_name.split('.').pop() || 'csv';
      a.download = `${site.name}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('CSV export downloaded');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to export to CSV');
    } finally {
      setExportingSiteIds(prev => {
        const next = new Set(prev);
        next.delete(site.id);
        return next;
      });
    }
  };

  const handleBulkExportExcel = async () => {
    try {
      const blob = await triggerBulkExportExcel().unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `as-builts-bulk-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Bulk Excel export downloaded');
    } catch (err: any) {
      const errorMessage = err?.data?.detail || err?.message || 'Failed to bulk export to Excel';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const executeDemoProgress = () => {
    const uploadId = `demo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const siteId = `unknown-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const fileName = `demo-as-built-${demoUploads.length + 1}.pdf`;

    const newUpload: DemoUpload = {
      id: uploadId,
      fileName: fileName,
      step: 'upload',
      siteId: siteId
    };

    setDemoUploads(prev => [...prev, newUpload]);

    setTimeout(() => {
      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'processing' } : upload
        )
      );

      setTimeout(() => {
        setDemoUploads(prev =>
          prev.map(upload =>
            upload.id === uploadId ? { ...upload, step: 'done' } : upload
          )
        );

        setTimeout(() => {
          setDemoUploads(prev => prev.filter(upload => upload.id !== uploadId));
        }, 5000);
      }, 2000);
    }, 2000);
  };

  const handleDemoProgress = () => {
    setPendingAction('demo');
    setShowConfirmModal(true);
  };

  const handleDemoFailure = () => {
    setPendingAction('failure');
    setShowConfirmModal(true);
  };

  const handleDemoFailureUpload = () => {
    setPendingAction('failure-upload');
    setShowConfirmModal(true);
  };

  const executeDemoFailure = () => {
    const uploadId = `demo-fail-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const siteId = `unknown-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newUpload: DemoUpload = {
      id: uploadId,
      fileName: `demo-as-built-${demoUploads.length + 1}.pdf`,
      step: 'upload',
      siteId: siteId
    };

    setDemoUploads(prev => [...prev, newUpload]);

    setTimeout(() => {
      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'processing' } : upload
        )
      );

      setTimeout(() => {
        setDemoUploads(prev =>
          prev.map(upload =>
            upload.id === uploadId ? { ...upload, step: 'failed', failureType: 'processing' } : upload
          )
        );
      }, 1000);
    }, 2000);
  };

  const executeDemoFailureUpload = () => {
    const uploadId = `demo-fail-upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const siteId = `unknown-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newUpload: DemoUpload = {
      id: uploadId,
      fileName: `demo-as-built-${demoUploads.length + 1}.pdf`,
      step: 'upload',
      siteId: siteId,
      showRetry: false
    };

    setDemoUploads(prev => [...prev, newUpload]);

    setTimeout(() => {
      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'failed', failureType: 'upload' } : upload
        )
      );
    }, 1000);
  };

  const handleCloseDemoProgress = (uploadId: string) => {
    setDemoUploads(prev => prev.filter(upload => upload.id !== uploadId));
  };

  const handleConfirmUpload = () => {
    setShowConfirmModal(false);
    if (pendingAction === 'demo') {
      executeDemoProgress();
    } else if (pendingAction === 'failure') {
      executeDemoFailure();
    } else if (pendingAction === 'failure-upload') {
      executeDemoFailureUpload();
    } else if (pendingAction === 'upload' && pendingFile) {
      executeUpload(pendingFile);
    } else if (pendingAction === 'bulk-upload' && pendingFiles.length > 0) {
      startBulkUpload();
    }
    setPendingAction(null);
    setPendingFile(null);
    setPendingFiles([]);
  };

  const handleCancelUpload = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
    setPendingFile(null);
    setPendingFiles([]);
  };

  const handleRetryDemoUpload = (uploadId: string) => {
    setDemoUploads(prev =>
      prev.map(upload =>
        upload.id === uploadId ? { ...upload, step: 'upload' } : upload
      )
    );

    setTimeout(() => {
      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'processing' } : upload
        )
      );

      setTimeout(() => {
        setDemoUploads(prev =>
          prev.map(upload =>
            upload.id === uploadId ? { ...upload, step: 'failed' } : upload
          )
        );
      }, 1000);
    }, 2000);
  };

  const handleBulkFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setError(null);

    if (files.length === 0) return;

    if (files.length > BULK_UPLOAD_LIMITS.MAX_FILES) {
      setError(`Maximum ${BULK_UPLOAD_LIMITS.MAX_FILES} files allowed`);
      return;
    }

    const maxFileSize = BULK_UPLOAD_LIMITS.MAX_FILE_SIZE_MB * 1024 * 1024;
    const maxTotalSize = BULK_UPLOAD_LIMITS.MAX_TOTAL_SIZE_MB * 1024 * 1024;
    let totalSize = 0;

    for (const file of files) {
      if (file.type !== 'application/pdf') {
        setError(`File "${file.name}" is not a PDF file`);
        return;
      }
      if (file.size > maxFileSize) {
        setError(`File too large. Maximum file size is ${BULK_UPLOAD_LIMITS.MAX_FILE_SIZE_MB}MB`);
        return;
      }
      totalSize += file.size;
    }

    if (totalSize > maxTotalSize) {
      setError(`Total size exceeds ${BULK_UPLOAD_LIMITS.MAX_TOTAL_SIZE_MB}MB limit`);
      return;
    }

    setPendingFiles(files);
    setPendingAction('bulk-upload');
    setShowConfirmModal(true);
  }, []);

  const handleUpload = useCallback(() => {
    setShowUploadModal(true);
  }, []);

  useEffect(() => {
    const handleTriggerUpload = () => {
      handleUpload();
    };

    window.addEventListener('triggerUploadAsBuilt', handleTriggerUpload);
    return () => {
      window.removeEventListener('triggerUploadAsBuilt', handleTriggerUpload);
    };
  }, [handleUpload]);

  const subscribeToProgress = useCallback((documentId: string, uploadId: string) => {
    if (!uploadTenantId) return;

    if (sseAbortRef.current[documentId]) {
      sseAbortRef.current[documentId].abort();
    }

    const abortController = new AbortController();
    sseAbortRef.current[documentId] = abortController;

    const subscribe = async () => {
      try {
        for await (const event of streamProgress(
          uploadTenantId,
          documentId,
          abortController.signal
        )) {
          setDemoUploads((prev) =>
            prev.map((upload) =>
              upload.id === uploadId
                ? {
                    ...upload,
                    step: event.status === 'completed' ? 'done' :
                          event.status === 'failed' ? 'failed' : 'processing',
                    documentId: event.document_id || upload.documentId,
                  }
                : upload
            )
          );

          if (event.status === 'completed' || event.status === 'failed') {
            delete sseAbortRef.current[documentId];
            
            if (event.status === 'completed') {
              setTimeout(() => {
                setDemoUploads(prev => prev.filter(upload => upload.id !== uploadId));
              }, 5000);
            }
            
            if (isSuperAdmin) {
              refetchAdminSites();
            } else if (selectedPortfolioId) {
              refetchPortfolioSites();
            }
            break;
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;

        if (err instanceof Error && (err.message.includes('401') || err.message.includes('Unauthorized'))) {
          delete sseAbortRef.current[documentId];
          await performGlobalLogout(dispatch, {
            toastMessage: 'Session expired. Please sign in again.',
            toastType: 'error',
          });
        } else {
          console.error('SSE error:', err);
          setDemoUploads((prev) =>
            prev.map((upload) =>
              upload.id === uploadId ? { ...upload, step: 'failed' } : upload
            )
          );
        }
      }
    };

    subscribe();
  }, [uploadTenantId, isSuperAdmin, selectedPortfolioId, refetchAdminSites, refetchPortfolioSites, dispatch]);

  const executeUpload = async (file: File) => {
    const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const siteId = `unknown-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    try {
      setError(null);

      if (!uploadTenantId) {
        throw new Error('Tenant ID is required for upload');
      }

      const newUpload: DemoUpload = {
        id: uploadId,
        fileName: file.name,
        step: 'upload',
        siteId: siteId,
      };
      setDemoUploads(prev => [...prev, newUpload]);

      const uploadResponse = await requestUpload({
        tenant_id: uploadTenantId as string,
        documents: [{
          filename: file.name,
          content_type: file.type || 'application/pdf',
          file_size: file.size,
          kind: 'pdf',
          doc_type: 'other',
          pipelines: ['asset'],
        }],
      }).unwrap();

      const result = uploadResponse.results[0];
      if (!result.success || !result.document_id || !result.upload_url || !result.storage_path) {
        throw new Error(result.error || 'Failed to get upload URL');
      }

      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId
            ? { ...upload, documentId: result.document_id, step: 'upload' }
            : upload
        )
      );

      const uploadResult = await fetch(result.upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/pdf',
        },
      });

      if (!uploadResult.ok) {
        throw new Error('Failed to upload file to storage');
      }

      await uploadCallback({
        document_id: result.document_id,
        storage_path: result.storage_path,
        size: file.size,
        content_type: file.type || 'application/pdf',
      }).unwrap();

      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'processing' } : upload
        )
      );

      subscribeToProgress(result.document_id, uploadId);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
      setError(errorMessage);
      toast.error(errorMessage);

      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'failed', failureType: 'upload' } : upload
        )
      );
    }
  };

  const startBulkUpload = async (filesToProcess?: Array<{ file: File; runExtraction?: boolean; documentType?: 'asbuilt' | 'event_log' }> | File[]) => {
    // Normalise: accept either enriched objects or plain File[]
    const normalised: Array<{ file: File; runExtraction?: boolean; documentType?: 'asbuilt' | 'event_log' }> = filesToProcess
      ? filesToProcess.map((f) => (f instanceof File ? { file: f } : f))
      : pendingFiles.map((f) => ({ file: f }));
    if (normalised.length === 0) return;

    setError(null);

    try {
      if (!uploadTenantId) {
        throw new Error('Tenant ID is required for upload');
      }

      const documents = normalised.map(({ file, runExtraction, documentType }) => ({
        filename: file.name,
        content_type: file.type || 'application/pdf',
        file_size: file.size,
        kind: 'pdf',
        doc_type: documentType === 'event_log' ? 'OTHER' : 'ASBUILT',
        pipeline: (documentType === 'event_log' ? 'intelligence' : 'knowledge') as UploadPipeline,
        run_extraction: documentType === 'event_log' ? true : runExtraction,
      }));

      const uploadResponse = await requestBulkUpload({
        tenant_id: uploadTenantId as string,
        documents,
      }).unwrap();

      const resultMap = new Map<string, typeof uploadResponse.results[0]>();
      uploadResponse.results.forEach((result) => {
        resultMap.set(result.filename, result);
      });

      const filesToUpload = normalised.filter(({ file }) => {
        const result = resultMap.get(file.name);
        return result?.success && result?.upload_url;
      });
      const concurrency = BULK_UPLOAD_LIMITS.CONCURRENT_UPLOADS;

      for (let i = 0; i < filesToUpload.length; i += concurrency) {
        const batch = filesToUpload.slice(i, i + concurrency);
        await Promise.all(batch.map(({ file, documentType }) => {
          const result = resultMap.get(file.name)!;
          return uploadBulkFile(file, result, documentType);
        }));
      }

      if (isSuperAdmin) {
        refetchAdminSites();
      } else if (selectedPortfolioId) {
        refetchPortfolioSites();
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk upload failed');
      toast.error(err instanceof Error ? err.message : 'Bulk upload failed');
    }
  };

  const uploadBulkFile = async (file: File, result: { upload_url?: string | null; storage_path?: string | null; document_id?: string | null }, documentType?: 'asbuilt' | 'event_log') => {
    if (!result.upload_url || !result.document_id || !result.storage_path) {
      const uploadId = `bulk-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const failedUpload: DemoUpload = {
        id: uploadId,
        fileName: file.name,
        step: 'failed',
        siteId: '',
        failureType: 'upload',
      };
      setDemoUploads(prev => [...prev, failedUpload]);
      return;
    }

    const uploadId = `bulk-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const siteId = `unknown-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newUpload: DemoUpload = {
      id: uploadId,
      fileName: file.name,
      step: 'upload',
      siteId: siteId,
      documentId: result.document_id,
    };
    setDemoUploads(prev => [...prev, newUpload]);

    try {
      const uploadResult = await fetch(result.upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/pdf',
        },
      });

      if (!uploadResult.ok) {
        throw new Error('Failed to upload file to storage');
      }

      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'processing' } : upload
        )
      );

      await uploadCallback({
        document_id: result.document_id,
        storage_path: result.storage_path,
        size: file.size,
        content_type: file.type || 'application/pdf',
      }).unwrap();

      subscribeToProgress(result.document_id, uploadId);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
      setError(errorMessage);
      toast.error(errorMessage);

      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'failed', failureType: 'upload' } : upload
        )
      );
    }
  };

  const handleModalUpload = (files: Array<{ file: File; runExtraction: boolean; documentType?: 'asbuilt' | 'event_log' }>) => {
    if (files.length === 0) return;
    // Skip the separate confirmation modal — the upload modal already confirmed
    startBulkUpload(files);
  };

  const validationStats = useMemo(() => {
    let passed = 0;
    let warnings = 0;
    let errors = 0;
    let pending = 0;
    let processing = 0;

    filteredSites.forEach((site) => {
      const cache = validationCache[site.id];
      const isAwaitingExtraction = site.meta?.awaiting_asbuilt_extraction === true;
      
      if (isAwaitingExtraction) {
        processing++;
      } else if (!cache || cache.status === 'loading' || cache.status === 'not_available') {
        pending++;
      } else if (cache.status === 'passed') {
        passed++;
      } else if (cache.status === 'warnings') {
        warnings++;
      } else if (cache.status === 'errors') {
        errors++;
      }
    });

    return { passed, warnings, errors, pending, processing, total: filteredSites.length };
  }, [filteredSites, validationCache]);

  if (loading || (!isSuperAdmin && !selectedPortfolioId)) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--color-zone-recessed)' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-engramiq-green animate-spin mx-auto mb-4" />
          <p className="eg-text-secondary">Loading sites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="eg-page-shell">
      <div className="eg-toolbar-header p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold eg-text-primary">Sites</h1>
            <p className="text-sm eg-text-secondary mt-1">
              {filteredSites.length} site{filteredSites.length !== 1 ? 's' : ''} in portfolio
            </p>
          </div>
          <div className="flex gap-3">
            {canUpload && (
              <button
                onClick={handleUpload}
                disabled={loading}
                className="px-4 py-2 bg-gradient-green-blue hover:brightness-110 rounded-lg text-sm text-engramiq-dark-blue hover:text-engramiq-dark-blue transition-all hover:shadow-engramiq flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="w-4 h-4" />
                Upload Documents
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-4 items-end flex-wrap">
          <div className="flex flex-col gap-1">
            <label className="eg-section-label">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="eg-input h-9 py-1.5"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="eg-section-label">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              onBlur={(e) => {
                const value = e.target.value;
                if (value) {
                  setAppliedDateFrom(dateFrom || '');
                  setAppliedDateTo(value);
                } else {
                  setAppliedDateFrom('');
                  setAppliedDateTo('');
                }
              }}
              className="eg-input h-9 py-1.5"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="eg-section-label">User</label>
            <div className="relative">
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="eg-select w-40 h-9 py-1.5"
              >
                {userOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 eg-text-primary pointer-events-none" />
            </div>
          </div>

          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label className="eg-section-label">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 eg-text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sites..."
                className="eg-input-search h-9 py-1.5"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="eg-page-content p-6">
        {demoUploads.length > 0 && (
          <div className="mb-4 space-y-3">
            {demoUploads.map((upload) => (
              <div key={upload.id} className="eg-card p-4">
                <ThreeStepProgress
                  currentStep={upload.step}
                  fileName={upload.fileName}
                  onClose={() => handleCloseDemoProgress(upload.id)}
                  onRetry={() => handleRetryDemoUpload(upload.id)}
                  showRetryButton={upload.showRetry !== false}
                  failureType={upload.failureType}
                />
              </div>
            ))}
          </div>
        )}

        {filteredSites.length === 0 && demoUploads.length === 0 ? (
          <div className="eg-empty-state">
            <FileText className="w-12 h-12 eg-text-muted mx-auto mb-4" />
            <p className="eg-empty-title">No sites found</p>
            <p className="eg-empty-text">Try adjusting your filters or upload a new site.</p>
          </div>
        ) : (
          <div className="eg-card-elevated overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="eg-table-header">
                  <th className="text-left p-4">
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center gap-2 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider hover:text-[var(--color-text-primary)] transition-colors"
                    >
                      Site Name
                      <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="text-left p-4">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center gap-2 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider hover:text-[var(--color-text-primary)] transition-colors"
                    >
                      Extraction Status
                      <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="text-left p-4">
                    <span className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
                      Drawing QA
                    </span>
                  </th>
                  <th className="text-left p-4">
                    <button
                      onClick={() => handleSort('created_at')}
                      className="flex items-center gap-2 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider hover:text-[var(--color-text-primary)] transition-colors"
                    >
                      Uploaded
                      <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="text-left p-4">
                    <span className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
                      Uploaded By
                    </span>
                  </th>
                  <th className="text-right p-4">
                    <span className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
                      Actions
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredSites.map((site, index) => {
                  const isSelected = currentSiteFromRedux?.site_id === site.id;
                  const validation = validationCache[site.id] || { status: 'not_available', errorCount: 0, warningCount: 0 };

                  return (
                    <tr
                      key={site.id}
                      onClick={() => handleSiteSelect(site.id)}
                      className={`eg-table-row group cursor-pointer active:scale-[0.995] active:bg-engramiq-green/20 ${
                        isSelected
                          ? 'bg-engramiq-green/10'
                          : index % 2 === 0
                          ? 'bg-transparent'
                          : 'eg-table-row-alt'
                      }`}
                    >
                      <td className="py-3 px-4">
                        {editingSiteId === site.id ? (
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              value={editedSiteName}
                              onChange={(e) => setEditedSiteName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEditedSiteName(site.id);
                                else if (e.key === 'Escape') cancelEditingSite();
                              }}
                              className="eg-input px-2 py-1"
                              autoFocus
                              disabled={savingSiteName}
                            />
                            <button
                              onClick={() => saveEditedSiteName(site.id)}
                              disabled={savingSiteName || isUpdatingSite}
                              className="p-1 bg-engramiq-green hover:bg-engramiq-green/80 rounded text-engramiq-dark-blue transition-all disabled:opacity-50"
                            >
                              {savingSiteName || isUpdatingSite ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={cancelEditingSite}
                              disabled={savingSiteName}
                              className="p-1 bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] rounded text-[var(--color-text-primary)] transition-all disabled:opacity-50"
                            >
                              <XIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[var(--color-text-primary)]">{site.name}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditingSite(site.id, site.name);
                              }}
                              className="p-1 opacity-0 group-hover:opacity-100 hover:bg-engramiq-green/10 rounded text-engramiq-green/70 hover:text-engramiq-green transition-all"
                              title="Rename site"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <ValidationStatusBadge
                          status={validation.status}
                          errorCount={validation.errorCount}
                          warningCount={validation.warningCount}
                          site={site}
                          onClick={async () => {
                            await handleSiteSelect(site.id);
                            window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'networkviewer' } }));
                          }}
                        />
                      </td>
                      <td className="py-3 px-4">
                        {(() => {
                          const qa = qaCache[site.id];
                          const badgeBase = "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer hover:scale-105 hover:shadow-lg";
                          const openQA = async (e: React.MouseEvent) => {
                            e.stopPropagation();
                            await handleSiteSelect(site.id);
                            window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'qaexecutivesummary' } }));
                          };
                          if (!qa || qa.status === 'loading') {
                            return (
                              <button onClick={openQA} className={`${badgeBase} bg-[var(--color-surface)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]`}>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Loading
                              </button>
                            );
                          }
                          if (qa.status === 'no_results') {
                            return (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-[var(--color-text-muted)]">
                                No QA run
                              </span>
                            );
                          }
                          if (qa.status === 'processing') {
                            // Check staleness at render time (> 3.5 hours)
                            const isStale = qa.createdAt && (Date.now() - new Date(qa.createdAt).getTime() > 3.5 * 60 * 60 * 1000);
                            if (isStale) {
                              return (
                                <button onClick={openQA} className={`${badgeBase} bg-red-400/20 text-red-400 border border-red-400/30 hover:bg-red-400/30 hover:border-red-400/50 cursor-help`} title="This extraction has been pending too long and likely failed. Please contact support.">
                                  <XCircle className="w-3.5 h-3.5" />
                                  <span className="font-bold">Failed</span>
                                </button>
                              );
                            }
                            return (
                              <button onClick={openQA} className={`${badgeBase} bg-engramiq-green/20 text-engramiq-green border border-engramiq-green/30 hover:bg-engramiq-green/30 hover:border-engramiq-green/50`}>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                <span className="font-bold">Processing</span>
                              </button>
                            );
                          }
                          return (
                            <button onClick={openQA} className={`${badgeBase} bg-engramiq-green/20 text-engramiq-green border border-engramiq-green/30 hover:bg-engramiq-green/30 hover:border-engramiq-green/50`}>
                              <CheckCircle className="w-3.5 h-3.5" />
                              <span className="font-bold text-green-400">{qa.totalPassed} passed</span>
                              <span className="opacity-50">/</span>
                              <span className="font-bold text-accent-red">{qa.totalFailed} failed</span>
                            </button>
                          );
                        })()}
                      </td>
                      <td className="py-2 px-4 text-sm text-[var(--color-text-secondary)]">
                        {formatDateTime(site.created_at)}
                      </td>
                      <td className="py-2 px-4 text-sm text-[var(--color-text-secondary)]">
                        {formatEmailDisplay(site.uploader?.email || site.uploaded_by)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          {(() => {
                            const isDownloading = downloadingDocIds.has(site.id);
                            const isExporting = exportingSiteIds.has(site.id);
                            const isProcessing = site.meta?.awaiting_asbuilt_extraction === true;
                            return (
                              <>
                                <div className="relative group/download">
                                  <button
                                    onClick={() => handleDownload(site)}
                                    disabled={isDownloading || isProcessing}
                                    className="p-1.5 bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] border border-[var(--color-border-secondary)] hover:border-engramiq-green rounded-lg text-engramiq-green transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {isDownloading ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Download className="w-4 h-4" />
                                    )}
                                  </button>
                                  <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-[var(--color-sidebar-tooltip-bg)] text-xs text-[var(--color-sidebar-tooltip-text)] rounded whitespace-nowrap opacity-0 group-hover/download:opacity-100 transition-opacity duration-150 shadow-lg z-50">
                                    {isDownloading ? 'Downloading...' : isProcessing ? 'Processing...' : 'Download PDF'}
                                  </span>
                                </div>
                                <div className="relative group/export">
                                  <button
                                    onClick={() => handleExportExcel(site)}
                                    disabled={isExporting || isProcessing}
                                    className="p-1.5 bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] border border-[var(--color-border-secondary)] hover:border-engramiq-green rounded-lg text-engramiq-green transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {isExporting ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <FileSpreadsheet className="w-4 h-4" />
                                    )}
                                  </button>
                                  <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-[var(--color-sidebar-tooltip-bg)] text-xs text-[var(--color-sidebar-tooltip-text)] rounded whitespace-nowrap opacity-0 group-hover/export:opacity-100 transition-opacity duration-150 shadow-lg z-50">
                                    {isExporting ? 'Exporting...' : isProcessing ? 'Processing...' : 'Export CSV'}
                                  </span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--color-table-border)]">
            <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
              <span>
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} sites
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1 || loading}
                className="px-3 py-1.5 bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] border border-[var(--color-border-secondary)] hover:border-engramiq-green rounded-lg text-engramiq-green transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Previous</span>
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, Math.ceil(totalItems / itemsPerPage)) }, (_, i) => {
                  const totalPages = Math.ceil(totalItems / itemsPerPage);
                  let pageNum: number;
                  
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  // For cursor-based pagination, we can only navigate to:
                  // - Current page (no action)
                  // - Next page (if available)
                  // - Previous pages we've visited (in cursor history)
                  const canNavigateToPage = 
                    pageNum === currentPage || 
                    (pageNum === currentPage + 1 && nextCursor) ||
                    (pageNum < currentPage && (currentPage - pageNum) <= cursorHistory.length);
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading || !canNavigateToPage}
                      className={`px-3 py-1.5 rounded-lg transition-all ${
                        currentPage === pageNum
                          ? 'bg-engramiq-green text-engramiq-dark-blue font-medium'
                          : canNavigateToPage
                          ? 'bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] text-[var(--color-text-primary)] border border-[var(--color-border-secondary)] hover:border-engramiq-green'
                          : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] border border-[var(--color-border)] cursor-not-allowed opacity-50'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={handleNextPage}
                disabled={!nextCursor || loading}
                className="px-3 py-1.5 bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] border border-[var(--color-border-secondary)] hover:border-engramiq-green rounded-lg text-engramiq-green transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <span>Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <UploadConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCancelUpload}
        onConfirm={handleConfirmUpload}
        fileCount={pendingFiles.length > 0 ? pendingFiles.length : (pendingFile ? 1 : 0)}
      />

      <UploadAsBuiltModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleModalUpload}
      />
    </div>
  );
};

export default SitesTableTab;
